"use client";

import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import React from "react";
import { usePopoverPosition } from "@/components/providers/PopoverPositionProvider";
import {
  breakpointClasses,
  cn,
  splitBreakpointProps,
  useMergeRefs,
} from "@/lib/utils";
import type { NavigationMenuTriggerProps } from "../types";

export const navigationMenuTriggerStyle = cva(
  "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 data-[state=open]:hover:bg-accent data-[state=open]:text-accent-foreground data-[state=open]:focus:bg-accent data-[state=open]:bg-accent/50 focus-visible:ring-ring/50 outline-none transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1",
);

function NavigationMenuTrigger({
  ref,
  className,
  children,
  ...props
}: NavigationMenuTriggerProps) {
  const { triggerRef } = usePopoverPosition();
  const mergedRef = useMergeRefs(ref, triggerRef);
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  return (
    <NavigationMenuPrimitive.Trigger
      ref={mergedRef}
      data-slot="navigation-menu-trigger"
      className={cn(navigationMenuTriggerStyle(), "group", className, classes)}
      {...native}
    >
      {children}{" "}
      <ChevronDown
        className="relative top-[1px] ml-1 size-3 transition duration-300 group-data-[state=open]:rotate-180"
        aria-hidden="true"
      />
    </NavigationMenuPrimitive.Trigger>
  );
}

NavigationMenuTrigger.displayName = "NavigationMenu.Trigger";

export default NavigationMenuTrigger;
